import { catchError, of, take } from 'rxjs';
import { identity, map, pickBy, range } from 'lodash';
import { Meta, Title } from '@angular/platform-browser';
import { Component, OnInit, ChangeDetectorRef, Renderer2, PLATFORM_ID, Inject } from '@angular/core';

import { AppService, LandingPagesService, SeoService, SlidesService, SharedService } from '../../services';
import {
  SlidesType,
  JobDefinitionType,
  LandingPageTypeEnum,
  JobDefinitionStatusEnum,
  LandingPagesWithoutTranslationType,
} from '@sifca-monorepo/ecommerce-generator';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

@Component({
  template: '',
})
export class BaseCareerListComponent implements OnInit {
  seo: any;
  currentPage = 1;
  numberOfPages = 3;
  startingPages = 0;
  posId: string = '';
  pageRange: number[];
  totalOfPages: number;
  jobsSlider: SlidesType;
  jobs: JobDefinitionType[];
  customersSlider: SlidesType;
  landingPages: LandingPagesWithoutTranslationType[];

  constructor(
    protected renderer: Renderer2,
    protected metaTagService: Meta,
    protected seoService: SeoService,
    protected appService: AppService,
    protected titleTagService: Title,
    protected sharedService: SharedService,
    protected slidesService: SlidesService,
    protected changeDetectorRef: ChangeDetectorRef,
    protected landingPagesService: LandingPagesService,
    @Inject(PLATFORM_ID) protected platformId: Object,
    @Inject(DOCUMENT) protected document: Document
  ) {
    this.sharedService.posId$.subscribe((posId) => {
      this.posId = posId;
    });
    this.landingPagesService
      .findLandingPagesByTargetAndTypePaginated([LandingPageTypeEnum.CAREER])
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((res) => {
        this.landingPages = res.objects;
        this.seoService
          .getPagesSEOJSON(this.landingPages[0].id)
          .pipe(
            take(1),
            catchError(() => {
              return of(null);
            }),
          )
          .subscribe((res) => {
            this.seo = res;
            if (this.seo?.title) {
              this.titleTagService.setTitle(this.seo?.title);
            }
            this.sharedService.tags$ = [
              ...this.metaTagService.addTags(map(this.seo?.metas, (meta) => pickBy(meta, identity))),
              { charset: 'UTF-8' },
            ];
              this.sharedService.links$ = map(this.seo?.links, (seoLink) => {
                const link = this.renderer.createElement('link');
                if (seoLink.sizes) {
                  link.sizes = seoLink.sizes;
                }
                if (seoLink.href) {
                  link.href = seoLink.href;
                }
                if (seoLink.rel) {
                  link.rel = seoLink.rel;
                }
                this.renderer.appendChild(this.document.head, link);
                return link;
              });
          });
        this.changeDetectorRef.markForCheck();
      });
    this.slidesService.slides$.subscribe((slides: SlidesType[]) => {
      if (slides) {
        this.jobsSlider = slides.find((slide) => slide.reference === 'jobs');
        this.customersSlider = slides.find((slide) => slide.reference === 'customers');
        setTimeout(() => {
          if (isPlatformBrowser(this.platformId)) {
            window.dispatchEvent(new Event('resize'));
          }
        }, 500);
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  ngOnInit(): void {
    this.appService
      .getJobDefinitionsByTargetAndStatusPagination(this.posId, JobDefinitionStatusEnum.ONGOING)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
    this.appService.jobs$.subscribe((jobs) => {
      if (jobs) {
        this.jobs = jobs.objects;
        this.totalOfPages = jobs.count / this.appService.jobLimit;
        if (this.numberOfPages < this.totalOfPages) {
          this.pageRange = range(this.startingPages, this.numberOfPages);
        } else {
          this.pageRange = range(this.startingPages, this.totalOfPages);
        }
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  getPage(page: number) {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
    this.appService.productsPageIndex$ = page;
    this.currentPage = page + 1;
    if (this.currentPage === this.pageRange.length && this.totalOfPages > this.numberOfPages + 3) {
      this.numberOfPages += 3;
      this.startingPages += 3;
      this.pageRange = range(this.startingPages, this.numberOfPages);
    } else {
      this.pageRange = range(this.startingPages, this.totalOfPages);
    }
    this.appService.getJobDefinitionsByTargetAndStatusPagination(this.posId, JobDefinitionStatusEnum.ONGOING).subscribe();
  }

  getLastPage() {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
    this.appService.productsPageIndex$ = this.totalOfPages - 1;
    this.pageRange = range(this.totalOfPages - 1, this.totalOfPages);
    this.currentPage = this.totalOfPages;
    this.appService
      .getJobDefinitionsByTargetAndStatusPagination(this.posId, JobDefinitionStatusEnum.ONGOING)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
  }

  getFirstPage() {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
    this.currentPage = 1;
    this.appService.productsPageIndex$ = 0;
    this.pageRange = range(0, 2);
    this.appService
      .getJobDefinitionsByTargetAndStatusPagination(this.posId, JobDefinitionStatusEnum.ONGOING)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
  }

  loadPrevPage() {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
    this.currentPage -= 1;
    this.appService.productsPageIndex$ -= 1;
    this.pageRange = range(0, 2);
    this.appService
      .getJobDefinitionsByTargetAndStatusPagination(this.posId, JobDefinitionStatusEnum.ONGOING)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
  }

  loadNextPage() {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
    this.numberOfPages++;
    this.currentPage += 1;
    this.appService.productsPageIndex$ += 1;
    this.pageRange = range(0, 2);
    if (this.currentPage > this.pageRange.length) {
      this.pageRange = range(0, this.numberOfPages);
    }
    this.appService
      .getJobDefinitionsByTargetAndStatusPagination(this.posId, JobDefinitionStatusEnum.ONGOING)
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
  }
}
